import { Tooltip, Typography, Skeleton } from 'antd';
import { StyledCard } from 'components/Custom/card';
import {
  formatMappingTaxRateLabel,
  getMappingSectionLabel,
  getMappingSubsectionLabel,
} from 'i18n/mappingSectionLabel';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TaxRateType } from 'store/app/enums';
import { MappingTaxRate } from 'store/returns/models/mappingTaxRate';
import { MappedReturn, Subsection } from 'store/returns/models/returnData';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { getReturnData } from 'store/returns/selectors';
import { useDispatch } from 'react-redux';
import { fetchAddReturnDataRequest } from 'store/returns/actions';
import { getReturnLoading } from 'store/returns/selectors';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
const MappingOverviewWrapper = styled.div`
  color: var(--color-text-base, #2e2e2e);

  .text-generic {
    color: var(--color-text-base, #2e2e2e);
    font-size: 13px;
    font-weight: 500;
  }

  .mapping-form-wrapper {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .section-title {
    font-family: var(--font-family, Inter, sans-serif);
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: var(--color-text-base, #2e2e2e);
    width: 100%;
  }

  .subsection-manual-box {
    height: 100%;
    border-radius: 5px;
    background: var(--color-surface, #f3f7f8);
    border: 1px solid var(--color-border, #dbeae3);
    display: flex;
    flex-direction: column;
  }

  .subsection-title {
    min-height: 30px;
    border-bottom: 1px solid var(--color-border, #dbeae3);
    padding: 10px;
    font-family: var(--font-family, Inter, sans-serif);
    font-weight: 500;
    font-size: 12px;
    line-height: 16px;
    color: var(--color-text-base, #2e2e2e);
    display: flex;
    align-items: center;
  }

  .mapping-box,
  .mapping-box-oos {
    min-width: 75px;
    height: 35px;
    border-radius: 2px;
    background: var(--color-status-warning-bg, #fff5e2);
    border: 1px solid var(--color-border, #dbeae3);
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-dark, #042a2a);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    align-self: center;
  }

  .mapping-box-oos {
    background: var(--color-status-danger-bg, #fce7e7);
  }

  .taxes-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: start;
    padding: 10px;
    height: 100%;
  }

  .tax-list-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: start;
    width: 75%;
  }

  .tax-rate-line {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-direction: row;
    font-family: var(--font-family, Inter, sans-serif);
    font-weight: 600;
    font-size: 13px;
    line-height: 24px;
    color: var(--color-text-base, #2e2e2e);
    margin-bottom: 3px;

    .ant-typography {
      color: inherit !important;
    }
  }

  .tax-rate-name {
    margin-right: 10px;
  }

  @media (max-width: 900px) {
    .section-title {
      font-size: 16px;
      line-height: 1.35;
    }

    .mapping-subsection-cell {
      width: 100% !important;
      margin-top: 12px !important;
    }

    .subsection-manual-box {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .taxes-wrapper {
      flex-direction: column;
      gap: 10px;
      align-items: stretch;
      padding: 10px;
    }

    .tax-list-wrapper {
      width: 100%;
    }

    .tax-rate-line {
      align-items: flex-start;
      gap: 8px;
      line-height: 1.35;
    }

    .tax-rate-name {
      margin-right: 0;
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
      overflow-wrap: anywhere;
    }

    .tax-rate-line > div:last-child {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      gap: 4px;
      min-width: 0;
    }

    .mapping-box,
    .mapping-box-oos {
      width: 100%;
      min-width: 0;
      align-self: stretch;
    }
  }

  .tax-rate-tags {
    padding: 0 5px 0 3px;
    margin-right: 5px;
    min-width: 30px;
    text-align: center;
    border-radius: 2px;
    font-size: 12px;
    font-weight: 600;
    line-height: 20px;
  }

  .tag-cash {
    background: var(--color-status-success-bg, #e9f6f7);
    color: var(--color-status-success, #058181);
  }

  .tag-rc {
    background: #ede5f7;
    color: #5a4a78;
  }

  .tag-input {
    background: var(--color-primary-soft, #e6f7f7);
    color: var(--color-text-dark, #042a2a);
  }

  .tag-output {
    background: var(--color-status-info-bg, #e1edf6);
    color: var(--color-text-dark, #042a2a);
  }

  .system-generated {
    height: 45px;
    background: var(--color-layout-bg, #e9f6f7);
    border-radius: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 10px;
  }

  .system-generated span {
    font-family: var(--font-family, Inter, sans-serif);
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    color: var(--color-text-base, #2e2e2e);
  }

  html[data-theme='dark'] & {
    .tag-cash {
      background: var(--color-status-success-bg, #132a27);
      color: var(--color-status-success, #68d7cb);
    }

    .tag-rc {
      background: #2a2238;
      color: #d4c4ef;
    }

    .tag-input {
      background: var(--color-primary, #62c8cd);
      color: var(--color-on-primary, #052325);
    }

    .tag-output {
      background: var(--color-status-info-bg, #16263a);
      color: var(--color-status-info, #8fbff5);
    }
  }
`;

const SYSTEM_GENERATED = 'SYSTEM_GENERATED';
interface MappingOverviewProps {
  //showSideMenu: boolean;
  mappedReturn?: MappedReturn;
  jsonMapping?: MappingTaxRate[];
}

const { Text } = Typography;
const CA = 'Cash Accounting';
const OUT_OF_SCOPE = 'OUT OF SCOPE';
export const MappingOverview: React.FC<MappingOverviewProps> = ({
  //showSideMenu,
  mappedReturn,
  jsonMapping,
}) => {
  const { t, i18n } = useTranslation();
  const pwaMobileNav = usePwaMobileNavEnabled();
  // Get sections from returns state as fallback
  const returnData = useSelector(getReturnData);
  const dispatch = useDispatch();
  const loading = useSelector(getReturnLoading);

  const sectionsReady =
    !!mappedReturn?.returnTypeId &&
    returnData?.connectionId === mappedReturn.connectionId &&
    returnData?.sectionsReturnTypeId === mappedReturn.returnTypeId;
  const requestedSectionsKey = useRef<string>();

  // Fetch this return type's sections when they are not already loaded.
  useEffect(() => {
    if (
      !mappedReturn?.connectionId ||
      !mappedReturn.returnTypeId ||
      sectionsReady ||
      loading
    ) {
      return;
    }
    const requestKey = `${mappedReturn.connectionId}:${mappedReturn.returnTypeId}`;
    if (requestedSectionsKey.current === requestKey) return;
    requestedSectionsKey.current = requestKey;
    dispatch(
      fetchAddReturnDataRequest(
        mappedReturn.connectionId,
        returnData?.connectionId === mappedReturn.connectionId
          ? returnData
          : undefined,
        mappedReturn.returnTypeId
      )
    );
  }, [
    dispatch,
    loading,
    mappedReturn?.connectionId,
    mappedReturn?.returnTypeId,
    returnData,
    sectionsReady,
  ]);

  // Use sections from mappedReturn if available, otherwise use sections from returnData
  const availableSections = mappedReturn?.sections || returnData?.sections;
  const returnTypeId = mappedReturn?.returnTypeId;

  const sections = availableSections?.filter((s) => {
    // If returnTypeId is not available, show all sections
    if (!returnTypeId) return true;

    return (
      s.returnTypeId === returnTypeId //||
      // (hasCA && s.name === s.subsections[0].code)
    );
  });
  sections?.sort((a, b) => a.sortOrder - b.sortOrder);
  // const jsonMappingString = mappedReturn?.dataLog?.jsonMapping;
  // const jsonMapping: MappingTaxRate[] = jsonMappingString
  //   ? JSON.parse(jsonMappingString).mapping
  //   : null;
  let cnt = 0;
  const setCount = (reset = false) => {
    if (reset) cnt = 0;
    else
      switch (cnt) {
        case 0:
          cnt = 1;
          break;
        case 1:
          cnt = 2;
          break;
        case 2:
          cnt = 1;
          break;
      }
  };

  const renderSubsections = (
    subsections: Subsection[],
    sectionName: string
  ) => {
    setCount(true);
    subsections?.sort((a, b) => a.sortOrder - b.sortOrder);

    const renderMargin = () => {
      if (cnt === 1) return { marginRight: 10 };
      if (cnt === 2) return { marginLeft: 10 };
    };

    const emptyCell = (boxName: string) => {
      const idx = jsonMapping?.findIndex((m) =>
        foundCode(m.codes || '', boxName)
      );
      if (idx === -1) return true;
      else return false;
    };

    const foundCode = (codeList: string, code2Find: string) => {
      const codesSplit = codeList?.split(',');
      const idx = codesSplit?.findIndex((c) => c === code2Find);
      if (idx === -1) return false;
      else return true;
    };

    return subsections?.map(
      (ss) =>
        ss.type !== SYSTEM_GENERATED && (
          <>
            {setCount()}
            <div
              className="mapping-subsection-cell"
              style={{
                width:
                  sectionName.toUpperCase() !== OUT_OF_SCOPE ? '50%' : '75%',
                marginTop: 20,
              }}
            >
              <div className="subsection-manual-box" style={renderMargin()}>
                <span className="subsection-title">
                  {getMappingSubsectionLabel(
                    ss.name.trim() ? ss.name : ss.code,
                    t
                  )}
                </span>

                <div className="taxes-wrapper">
                  <div className="tax-list-wrapper">
                    {jsonMapping?.map(
                      (m) =>
                        foundCode(m.codes || '', ss.code) && (
                          <>
                            <div className="tax-rate-line">
                              <Typography.Text
                                className="tax-rate-name"
                                ellipsis={
                                  !pwaMobileNav &&
                                  sectionName.toUpperCase() !== OUT_OF_SCOPE //@ts-ignore
                                    ? { rows: 1, tooltip: true }
                                    : false
                                }
                              >
                                {formatMappingTaxRateLabel(
                                  m.taxCodeName,
                                  m.taxRate,
                                  m.reverseCharge,
                                  i18n.language
                                )}
                              </Typography.Text>
                              <div style={{ display: 'flex' }}>
                                {m.codes?.includes(CA) && (
                                  <Tooltip title={t('returns.mapping.guide.cash-accounting-title')}>
                                    <div className="tax-rate-tags tag-cash">
                                      {t('returns.mapping.side-panel.cash-tag')}
                                    </div>
                                  </Tooltip>
                                )}
                                {m.reverseCharge && (
                                  <Tooltip title={t('returns.mapping.guide.reverse-charge-title')}>
                                    <div className="tax-rate-tags tag-rc">
                                      RC
                                    </div>
                                  </Tooltip>
                                )}
                                {m.taxRateType === TaxRateType.INPUT &&
                                  sectionName?.toUpperCase() !==
                                    OUT_OF_SCOPE && (
                                    <Tooltip title={t('returns.mapping.input')}>
                                      <div className="tax-rate-tags tag-input">
                                        {t('returns.mapping.side-panel.input-tag')}
                                      </div>
                                    </Tooltip>
                                  )}
                                {m.taxRateType === TaxRateType.OUTPUT && (
                                  <Tooltip title={t('returns.mapping.output')}>
                                    <div className="tax-rate-tags tag-output">
                                      {t('returns.mapping.side-panel.output-tag')}
                                    </div>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </>
                        )
                    )}
                    {emptyCell(ss.code) && (
                      <span
                        className="subsection-title"
                        style={{ border: 'none' }}
                      >
                        {t('returns.mapping.side-panel.no-tax-rates-mapped')}
                      </span>
                    )}
                  </div>
                  <div className="mapping-box">
                    {getMappingSubsectionLabel(ss.code, t)}
                  </div>
                </div>
              </div>
            </div>
            {/* )} */}
            {/* </div> */}
          </>
        )
    );
  };

  return (
    <MappingOverviewWrapper className="mapping-overview-wrapper">
      {' '}
      <StyledCard //@ts-ignore
        style={{
          //width: showSideMenu ? 'calc(100vw - 550px)' : '100%',
          //minWidth: 900,
          maxWidth: 1000,
          margin: 'auto',
        }}
      >
        <Text className="text-generic">
          {t('returns.mapping.submission-readonly-notice')}
        </Text>

        {loading ? (
          <>
            <div className="divider-20" />
            <div className="divider-20" />
            {/* Section Title Skeleton */}
            <Skeleton.Input 
              active 
              size="default" 
              style={{ 
                width: '300px', 
                height: '24px', 
                marginBottom: '20px',
                borderRadius: '4px'
              }} 
            />
            
            {/* Mapping Boxes Skeleton - 2 columns, 3 rows */}
            <div className="mapping-form-wrapper">
              {[1, 2, 3, 4, 5, 6].map((boxIndex) => {
                // Determine if it's the first or second box in the pair (odd = first, even = second)
                const isFirstInPair = boxIndex % 2 === 1;
                
                return (
                <div
                  key={boxIndex}
                  style={{
                    width: '50%',
                    marginTop: 20,
                  }}
                >
                  <div 
                    className="subsection-manual-box"
                    style={{
                      background: 'var(--color-surface, #f3f7f8)',
                      borderRadius: '5px',
                      display: 'flex',
                      flexDirection: 'column',
                      marginRight: isFirstInPair ? 10 : 0,
                      marginLeft: !isFirstInPair ? 10 : 0,
                    }}
                  >
                    {/* Box Title Skeleton */}
                    <div 
                      className="subsection-title"
                      style={{
                        minHeight: '30px',
                        borderBottom: '1px solid var(--color-border, #dbeae3)',
                        padding: '10px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Skeleton.Input 
                        active 
                        size="default" 
                        style={{ 
                          width: '100%', 
                          height: '16px',
                          borderRadius: '4px'
                        }} 
                      />
                    </div>
                    
                    {/* Box Content Skeleton */}
                    <div 
                      className="taxes-wrapper"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'start',
                        padding: '10px',
                        height: '100%',
                      }}
                    >
                      <div 
                        className="tax-list-wrapper"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'start',
                          width: '75%',
                        }}
                      >
                        {/* "No tax rates" message skeleton */}
                        <Skeleton.Input 
                          active 
                          size="default" 
                          style={{ 
                            width: '200px', 
                            height: '16px',
                            marginTop: '10px',
                            borderRadius: '4px'
                          }} 
                        />
                      </div>
                      
                      {/* Box Label Skeleton */}
                      <div 
                        className="mapping-box"
                        style={{
                          minWidth: '75px',
                          height: '35px',
                          borderRadius: '2px',
                          background: 'var(--color-status-warning-bg, #fff5e2)',
                          border: '1px solid var(--color-border, #dbeae3)',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          padding: '10px',
                          alignSelf: 'center',
                        }}
                      >
                        <Skeleton.Input 
                          active 
                          size="default" 
                          style={{ 
                            width: '50px', 
                            height: '20px',
                            borderRadius: '4px'
                          }} 
                        />
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </>
        ) : sections && sections.length > 0 ? (
          <div className="mapping-form-wrapper">
            {sections?.map(
              (s) =>
                s.subsections.findIndex((ss) => ss.type !== SYSTEM_GENERATED) >
                  -1 && (
                  <>
                    <div className="divider-20" />
                    <div className="divider-20" />
                    <div className="section-title">
                      {getMappingSectionLabel(s.name, t, i18n.language)}
                    </div>
                    {renderSubsections(s.subsections, s.name)}
                  </>
                )
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Text style={{ color: 'var(--color-text-base, #2e2e2e)' }}>
              {t('organisation.no-sections-available-for-this-mapping')}
            </Text>
          </div>
        )}
      </StyledCard>
    </MappingOverviewWrapper>
  );
};
